/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import VoucherAction from "store/shop/VoucherAction";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import {toast} from "react-toastify";
import BookingAction from "store/booking/BookingAction";
import {VoucherItem} from "types/entities/VoucherItem";
import {isAlreadyApplied, useVouchersWithValidOnDate} from "utils/voucherUtils";
import clsx from "clsx";
import BookingVoucherItem from "pages/Front/Booking/components/BookingVoucherItem";
import {ThunkAction} from "redux-thunk";
import {Action} from "redux";
import {
    DEFAULT_HALL,
    DEFAULT_HALL_ID,
    FELIXTAG_VOUCHER_BANNER_ENABLED,
    IS_VOUCHER_ALLOWED_IN_SHOPPING_CART,
    VOUCHERS_LIMIT,
} from "helpers/config";
import {HallCode} from "types/enums/HallCode";
import CardLabel from "pages/Front/Booking/components/BookingCustomer/CardLabel";
import {useHallOpening} from "utils/hooks/useHallOpening";

interface BookingVoucherProps {
    isFilter?: boolean;
}

const BookingVoucher = (props: BookingVoucherProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const voucherItems = useTypedSelector(state => state.voucherState.voucherItems);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const appliedVoucherItems = useTypedSelector(state => state.voucherState.appliedVoucherItems);
    const [applyPending, setApplyPending] = useState(false);
    const hallOpening = useHallOpening();
    const voucherUtils = useVouchersWithValidOnDate(hallOpening);
    const defaultValues = {
        code: "",
    };

    const {register, handleSubmit, reset} = useForm({
        defaultValues: defaultValues,
    });

    const fetchGetVoucher = (data, dispatch) => {
        return dispatch(VoucherAction.fetchGetVoucher(data))
            .then(r => {
                if (r.voucher) {
                    if (r.voucher.hall_id === DEFAULT_HALL_ID) {
                        dispatch(VoucherAction.setVoucherItem(r.voucher));
                    } else {
                        toast.warning(t("Voucher invalid"));
                    }
                }

                voucherUtils.dispatchFilterIfVoucherHasDate(r.voucher);
                if (r.packageCart) {
                    dispatch(BookingAction.setPackageCart(r.packageCart));
                }
                return r;
            })
            .catch(err => {
                toast.error(err);
            });
    };

    const fetchVoucherAndApply = data => {
        if (packagesCart && !props.isFilter) {
            dispatch<ThunkAction<any, any, any, Action<VoucherAction>>>(VoucherAction.fetchApplyVoucher(packagesCart, data))
                .then(r => {
                    if (r.packageCart) {
                        dispatch(BookingAction.setPackageCart(r.packageCart));
                    }
                    if (r.voucher) {
                        dispatch(VoucherAction.setVoucherItem(r.voucher));
                        dispatch(VoucherAction.applyVoucherItem(r.voucher));
                        setApplyPending(false);
                    }
                    return r;
                })
                .catch(err => {
                    toast.error(err);
                });
        }
    };
    const submitFormHandler = data => {
        if (voucherItems.length >= VOUCHERS_LIMIT) {
            toast.warning(t("Only one coupon can be applied"));
            return;
        }
        if (data.code.length <= 2) {
            toast.warning(t("Code has to be filled in"));
            return;
        }
        if (data.code.trim().split(" ").length - 1 > 0) {
            toast.warning(t("Code has to be filled in without spaces"));
            return;
        }
        if (typeof packagesCart === "undefined" || packagesCart == null) {
            fetchGetVoucher(data, dispatch).then(r => {
                reset({"code": ""});
            });
            return;
        }
        const applied = isAlreadyApplied(appliedVoucherItems, data.code);

        if (packagesCart && !props.isFilter) {
            if (applied.length === 0) {
                setApplyPending(true);
                fetchVoucherAndApply(data);
                reset({"code": ""});
            }
            if (applied.length > 0) {
                reset({"code": ""});
                toast.warning(t("Code is already applied"));
            }
            return;
        }

        if (packagesCart && props.isFilter) {
            if (applied.length === 0) {
                setApplyPending(true);
                fetchGetVoucher(data, dispatch).then(r => {
                    setApplyPending(false);
                });
                reset({"code": ""});
            }
            if (applied.length > 0) {
                reset({"code": ""});
                toast.warning(t("Code is already applied"));
            }
            return;
        }
        setApplyPending(false);
        toast.error(t("Cart is not valid"));
    };

    const onKeyDownHandler = (event, callback) => {
        if (event.keyCode == 13) {
            event.preventDefault();
            return callback();
        }
    };
    const deleteVoucherHandler = async (code: string) => {
        const voucherItemToDelete = voucherItems.find(voucherItem => voucherItem.code === code);
        const appliedItemToDelete = appliedVoucherItems.find(voucherItem => voucherItem.code === code);
        if (packagesCart && appliedItemToDelete) {
            dispatch<ThunkAction<any, any, any, Action<VoucherAction>>>(VoucherAction.fetchDeleteVoucher(packagesCart, appliedItemToDelete)).then(r => {
                dispatch(VoucherAction.deleteAppliedVoucherItem(appliedItemToDelete));

                if (r.packageCart) {
                    dispatch(BookingAction.setPackageCart(r.packageCart));
                    toast.warning(t("Cart has been updated"));
                }
            });
        }

        if (voucherItemToDelete) {
            dispatch(VoucherAction.deleteVoucherItem(voucherItemToDelete));
        }
    };

    useEffect(() => {
        if (voucherItems.length > 0) {
            if (!props.isFilter && packagesCart) {
                dispatch<ThunkAction<any, any, any, Action<BookingAction>>>(BookingAction.fetchPackageCart(packagesCart)).then(r => {});
            }
        }
    }, [voucherItems]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            voucherItems.map(voucherItem => {
                if (typeof voucherItem.code === "string" && voucherItem.code.length > 0) {
                    if (!voucherItem.validity) {
                        return null;
                    }
                    const applied = isAlreadyApplied(appliedVoucherItems, voucherItem.code);
                    if (applied.length === 0) {
                        if (!applyPending) {
                            if (voucherItem.amount_left > 0) {
                                fetchVoucherAndApply(voucherItem);
                            } else {
                            }
                        }
                    }
                }
            });
        }, 500);

        return () => {
            clearTimeout(timeout);
        };
    }, [packagesCart]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (!packagesCart || !packagesCart.packagesCartItemVouchers) {
                return;
            }

            const vouchersInPackagesCart = packagesCart.packagesCartItemVouchers.map(packagesCartItemVoucherItem => {
                return packagesCartItemVoucherItem.voucher.code;
            });

            voucherItems.map(voucherItem => {
                if (!vouchersInPackagesCart.includes(voucherItem.code)) {
                    fetchVoucherAndApply(voucherItem);
                }
            });
        }, 1000);

        return () => {
            clearTimeout(timeout);
        };
    }, [packagesCart]);

    useEffect(() => {
        voucherUtils.checkIfVouchersChangeTheDate();
    }, [voucherItems]);

    return (
        <div
            className={clsx(
                !props.isFilter && "mt-2",
                !IS_VOUCHER_ALLOWED_IN_SHOPPING_CART && !props.isFilter && "!hidden",
                "BookingVoucher relative flex flex-col space-y-8 w-full justify-between"
            )}
        >
            <CardComponent>
                <div className={""}>
                    {FELIXTAG_VOUCHER_BANNER_ENABLED && (
                        <picture className={"FelixtagBanner absolute top-1 w-32"}>
                            <img alt={"Felixtag Gutschein"} src={"/img/felixtag.svg"} />
                        </picture>
                    )}
                </div>
                <div className={"BookingVoucherTitle mb-2"}>
                    <CardLabel title={t("Voucher code")} />
                </div>
                <div className={"z-40 flex flex-row justify-between space-x-2"}>
                    <input
                        className={"shrink w-1/2 border border-black px-1 rounded-md"}
                        type={"text"}
                        {...register("code")}
                        placeholder={t("Enter code here")}
                        onKeyDown={e => {
                            onKeyDownHandler(e, handleSubmit(submitFormHandler));
                        }}
                    />
                    <button
                        className={clsx(
                            DEFAULT_HALL === HallCode.Zwolle ? "bg-cta-800 text-white border-cta-800 " : "bg-primary-800 text-white border-primary-800 ",
                            "ButtonBookingVoucher w-1/2 rounded-md  hover:no-underline h-10 -mr-px grow inline-flex items-center text-center justify-center px-1 py-2 text-base sm:leading-3",
                            "bg-primary-800 text-white disabled:bg-gray-500 disabled:cursor-not-allowed"
                        )}
                        type={"button"}
                        onClick={handleSubmit(submitFormHandler)}
                    >
                        {t("Apply code")}
                    </button>
                </div>
                <div className={clsx(voucherItems.length > 0 ? "max-h-[1000px]" : "max-h-0", "transform-gpu duration-500 ease-out")}>
                    {((voucherItems && voucherItems.length > 0) || (appliedVoucherItems && appliedVoucherItems.length > 0)) && (
                        <div className={"space-y-3"}>
                            <hr className={"border-gray-700 mt-4"} />
                            <h3 className={"font-bold"}>{t("Vouchers")}:</h3>
                            <div className={"divide-y divide-gray-600 space-y-1"}>
                                {voucherItems.map((voucherItem: VoucherItem) => {
                                    const foundVoucher = appliedVoucherItems.find(appliedVoucherItem => appliedVoucherItem.code === voucherItem.code);
                                    if (voucherItem === null) {
                                        return false;
                                    }
                                    return (
                                        <div key={voucherItem.id} className={"py-1"}>
                                            <BookingVoucherItem
                                                foundVoucher={typeof foundVoucher !== "undefined"}
                                                voucherItem={voucherItem}
                                                onClick={() => deleteVoucherHandler(voucherItem.code)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </CardComponent>
        </div>
    );
};

export default BookingVoucher;
